import {
  isInternalSlackConversationName,
  type SlackParentMessage,
} from '@littlebird/action-cards';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { useState } from 'react';

import { Avatar, InitialsAvatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';

type ActionCardContextNoteProps = {
  context?: string;
  quotedMessage?: SlackParentMessage | null;
  className?: string;
};

type ClampedNoteTextProps = {
  text: string;
  searchKey: string;
  italic?: boolean;
  href?: string | null;
};

type SlackQuoteProps = {
  parent: SlackParentMessage;
};

const CLAMP_THRESHOLD_CHARS = 220;

const ClampedNoteText = ({
  text,
  searchKey,
  italic,
  href,
}: ClampedNoteTextProps) => {
  const [expanded, setExpanded] = useState(false);
  const clampable =
    text.length > CLAMP_THRESHOLD_CHARS || text.split('\n').length > 3;
  const textNode = (
    <span
      data-find-field={searchKey}
      className={cn(
        'whitespace-pre-wrap break-words text-[0.8125rem] leading-relaxed text-foreground-subtle',
        italic &&
          'italic text-[color-mix(in_srgb,var(--foreground-subtle),var(--foreground-muted))]',
        clampable && !expanded && 'line-clamp-3'
      )}
    >
      {text}
    </span>
  );
  return (
    <>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
          style={{ color: 'inherit' }}
          className="no-underline underline-offset-2 hover:underline focus-visible:underline"
        >
          {textNode}
        </a>
      ) : (
        textNode
      )}
      {clampable ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="self-start text-xs font-medium text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </>
  );
};

const SlackQuote = ({ parent }: SlackQuoteProps) => {
  const seconds = Number(parent.timestamp.split('.')[0]);
  const relativeTime = Number.isFinite(seconds)
    ? formatDistanceToNow(new Date(seconds * 1000), { addSuffix: true })
    : null;
  const channelName = isInternalSlackConversationName(parent.channel_name)
    ? null
    : parent.channel_name;
  const meta = [channelName, relativeTime].filter(Boolean).join(' · ');
  return (
    <span
      role="note"
      aria-label="Quoted Slack message"
      className="flex gap-2.5"
    >
      <Avatar
        avatarUrl={parent.person.avatar_url}
        alt={parent.person.name}
        fallback={
          <InitialsAvatar
            name={parent.person.name}
            seed={parent.person.id}
            className="size-7"
          />
        }
        containerClassName="size-7 shrink-0 overflow-hidden rounded-full"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
          <span className="truncate font-medium text-foreground">
            {parent.person.name}
          </span>
          {meta ? (
            <span className="truncate text-foreground-muted">{meta}</span>
          ) : null}
        </span>
        <ClampedNoteText
          text={parent.text}
          searchKey="quote"
          href={parent.message_link}
        />
      </span>
    </span>
  );
};

export const ActionCardContextNote = ({
  context,
  quotedMessage,
  className,
}: ActionCardContextNoteProps) => {
  const parentText = quotedMessage?.text.trim() ?? '';
  const showQuote = parentText.length > 0;
  const contextText = context?.trim() ?? '';
  const contextRepeatsQuote = showQuote && contextText === parentText;
  const showContext = contextText.length > 0 && !contextRepeatsQuote;

  if (!showQuote && !showContext) return null;

  return (
    <span className={cn('flex flex-col gap-2 py-1', className)}>
      {showQuote && quotedMessage ? (
        <SlackQuote parent={quotedMessage} />
      ) : null}
      {showContext ? (
        <span role="note" aria-label="Context" className="flex">
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <ClampedNoteText text={contextText} searchKey="context" italic />
          </span>
        </span>
      ) : null}
    </span>
  );
};
